import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MapPin,
  Edit2,
  Trash2,
  X,
  Building,
  AlertCircle
} from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lista de estados brasileiros
const ESTADOS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

export default function Locations({ userRole = 'admin' }) {
  const canEdit = userRole === 'admin';
  
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [form, setForm] = useState({
    name: '',
    city: '',
    state: '',
    notes: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      } else {
        toast.error('Erro ao carregar locais');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingLocation 
      ? `${API_URL}/api/locations/${editingLocation.id}`
      : `${API_URL}/api/locations`;
    
    try {
      const response = await fetch(url, {
        method: editingLocation ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingLocation ? 'Local atualizado!' : 'Local cadastrado!');
        setShowModal(false);
        resetForm();
        fetchLocations();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este local?')) return;

    try {
      const response = await fetch(`${API_URL}/api/locations/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        toast.success('Local excluído!');
        fetchLocations();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setForm({
      name: location.name,
      city: location.city,
      state: location.state,
      notes: location.notes || ''
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingLocation(null);
    setForm({
      name: '',
      city: '',
      state: '',
      notes: ''
    });
  };

  const filteredLocations = locations.filter(l => {
    const searchLower = search.toLowerCase();
    return (
      l.name.toLowerCase().includes(searchLower) ||
      l.city.toLowerCase().includes(searchLower) ||
      l.state.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Locais de Operação</h1>
          <p className="text-gray-400 mt-1">Cadastro de locais para ordens de serviço</p>
        </div>
        {canEdit && (
          <button
            onClick={openNewModal}
            data-testid="add-location-button"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
          >
            <Plus size={20} />
            <span>Novo Local</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar por nome, cidade ou estado..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
          data-testid="search-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredLocations.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredLocations.map((location) => (
            <div 
              key={location.id} 
              className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-all"
              data-testid={`location-card-${location.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <MapPin size={20} className="text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{location.name}</h3>
                    <div className="flex items-center gap-1 text-gray-400 text-sm mt-1">
                      <Building size={14} />
                      <span>{location.city} - {location.state}</span>
                    </div>
                    {location.notes && (
                      <p className="text-gray-500 text-xs mt-2">{location.notes}</p>
                    )}
                  </div>
                </div>
                
                {canEdit && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(location)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                      data-testid={`edit-location-${location.id}`}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      data-testid={`delete-location-${location.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-12 text-center">
          <AlertCircle size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-white text-lg font-semibold mb-2">Nenhum local encontrado</h3>
          <p className="text-gray-500 mb-6">
            {search ? 'Tente ajustar sua busca' : 'Comece cadastrando locais de operação'}
          </p>
          {!search && canEdit && (
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              Cadastrar Local
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">
                {editingLocation ? 'Editar Local' : 'Novo Local de Operação'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Nome do Local *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                  placeholder="Ex: Fazenda Ouro Verde"
                  required
                  data-testid="input-name"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Cidade *</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({...form, city: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                  placeholder="Ex: Santa do Oeste"
                  required
                  data-testid="input-city"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Estado *</label>
                <select
                  value={form.state}
                  onChange={(e) => setForm({...form, state: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                  required
                  data-testid="input-state"
                >
                  <option value="">Selecione o estado</option>
                  {ESTADOS.map(uf => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                  placeholder="Informações adicionais..."
                  rows={2}
                  data-testid="input-notes"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
                  data-testid="submit-location"
                >
                  {editingLocation ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
